import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { SectionHeader } from "@/components/common/Widgets";
import { fmtNum, fmtDate } from "@/lib/format";
import { Banknote, Link2, Loader2 } from "lucide-react";

export default function AASection({ app, customer, consents, fiData, reload }) {
  const [fips, setFips] = useState([]);
  const [selected, setSelected] = useState([]);
  const [mobile, setMobile] = useState(customer?.mobile || "");
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(null);

  useEffect(() => {
    api.get("/aa/fips").then(r => {
      const list = r.data.fips || [];
      setFips(list);
      // Default-select the bank tied to this application (or the first FIP)
      if (list.length > 0) {
        setSelected((cur) => (cur.length > 0 ? cur : [list[0].id]));
      }
    });
  }, []);
  useEffect(() => () => { if (polling) clearInterval(polling); }, [polling]);

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const startConsent = async () => {
    if (!mobile) return toast.error("Customer mobile required");
    if (selected.length === 0) return toast.error("Select at least one FIP");
    setBusy(true);
    try {
      const res = await api.post("/aa/consent/request", { application_id: app.id, customer_mobile: mobile, customer_pan: customer?.pan, fip_ids: selected });
      toast.success("Consent request created — auto-activates in 5s (mock)");
      reload && reload();
      // Poll for activation
      let count = 0;
      const id = setInterval(async () => {
        count++;
        const r = await api.get(`/aa/consent/${res.data.consent_handle}`);
        if (r.data.status === "ACTIVE" || count > 8) { clearInterval(id); setPolling(null); reload && reload(); if (r.data.status === "ACTIVE") toast.success("Consent ACTIVE"); }
      }, 1500);
      setPolling(id);
    } catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };
  const fetchFI = async (handle) => {
    try {
      await api.post("/aa/fi/request", { consent_handle: handle });
      toast.success("FI data fetched");
      reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3">
      <SectionHeader title="Account Aggregator (AA)" subtitle={<span className="flex items-center gap-1"><Badge variant="outline" className="text-[10px]">MOCKED</Badge> Follows RBI's Sahamati AA spec. Pull bank statements with customer consent.</span>} />

      <Card className="p-4 space-y-3">
        <div className="text-sm font-semibold heading flex items-center gap-2"><Link2 className="h-4 w-4 text-accent" />Initiate Consent</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Customer Mobile"><Input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="9876543210" data-testid="aa-mobile-input" /></Field>
          <Field label="Selected FIPs">
            <div className="flex flex-wrap gap-2 mt-1">
              {fips.map(f => (
                <button key={f.id} type="button" onClick={() => toggle(f.id)} className={`px-2.5 py-1 rounded-full text-xs border ${selected.includes(f.id) ? "bg-accent text-accent-foreground border-accent" : "bg-card border-border hover:border-accent/40"}`} data-testid={`aa-fip-${f.id}`}>
                  {f.name}
                </button>
              ))}
            </div>
          </Field>
        </div>
        <div className="flex justify-end"><Button onClick={startConsent} disabled={busy} data-testid="aa-consent-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4 mr-1" />}Initiate Consent</Button></div>
      </Card>

      <Card className="p-4">
        <div className="text-sm font-semibold heading mb-3">Consent Handles</div>
        {(consents || []).length === 0 && <div className="text-sm text-muted-foreground">No consents yet.</div>}
        <ul className="divide-y divide-border">
          {(consents || []).map(c => (
            <li key={c.id} className="py-3 flex flex-wrap items-center gap-3">
              <Badge variant="outline" className={c.status === "ACTIVE" ? "text-success border-success/40" : c.status === "PENDING" ? "text-warning border-warning/40" : ""}>{c.status}</Badge>
              <div className="flex-1 min-w-0">
                <div className="font-medium mono text-xs">{c.consent_handle}</div>
                <div className="text-[11px] text-muted-foreground mono">{fmtDate(c.created_at)} · FIPs: {(c.fip_ids || []).join(", ")}</div>
              </div>
              <Button size="sm" variant="outline" disabled={c.status !== "ACTIVE"} onClick={() => fetchFI(c.consent_handle)} data-testid={`aa-fetch-${c.id}`}>Fetch FI Data</Button>
            </li>
          ))}
        </ul>
      </Card>

      {(fiData || []).map((f) => (
        <Card key={f.id} className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold heading flex items-center gap-2"><Banknote className="h-4 w-4 text-accent" />FI Data · <span className="mono text-xs">{f.request_id}</span></div>
            <div className="text-[11px] text-muted-foreground mono">{fmtDate(f.requested_at)}</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(f.data || {}).map(([fipId, d]) => (
              <Card key={fipId} className="p-3 border-border">
                <div className="flex items-center justify-between">
                  <div className="font-medium">{d.fip?.name || fipId}</div>
                  <Badge variant="secondary" className="text-xs">{d.account_type}</Badge>
                </div>
                <div className="text-[11px] text-muted-foreground mono mt-0.5">A/C: {d.account_no}</div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <Mini k="Credits (6m)" v={`₹ ${fmtNum(d.summary.total_credits / 100000, 2)} L`} />
                  <Mini k="Debits (6m)" v={`₹ ${fmtNum(d.summary.total_debits / 100000, 2)} L`} />
                  <Mini k="Avg Bal" v={`₹ ${fmtNum(d.summary.avg_monthly_balance / 100000, 2)} L`} />
                  <Mini k="Txns" v={d.summary.transaction_count} />
                </div>
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-accent">View transactions ({d.transactions?.length || 0})</summary>
                  <div className="max-h-64 overflow-y-auto mt-2 border rounded-md">
                    <table className="w-full text-[11px]">
                      <thead className="bg-muted/60 text-muted-foreground sticky top-0"><tr><th className="text-left px-2 py-1">Date</th><th className="text-left px-2 py-1">Narration</th><th className="text-right px-2 py-1">Amount</th><th className="text-right px-2 py-1">Balance</th></tr></thead>
                      <tbody>{(d.transactions || []).slice(0, 80).map((t, i) => (
                        <tr key={i} className="border-t border-border"><td className="px-2 py-1 mono">{t.date}</td><td className="px-2 py-1">{t.narration}</td><td className={`px-2 py-1 num-right ${t.type === "CREDIT" ? "text-success" : "text-destructive"}`}>{t.type === "CREDIT" ? "+" : "−"}{fmtNum(t.amount, 0)}</td><td className="px-2 py-1 num-right">{fmtNum(t.balance, 0)}</td></tr>
                      ))}</tbody>
                    </table>
                  </div>
                </details>
              </Card>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}
function Field({ label, children }) { return (<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{label}</Label>{children}</div>); }
function Mini({ k, v }) { return (<div className="rounded-md border p-2"><div className="text-[10px] uppercase text-muted-foreground">{k}</div><div className="text-sm font-semibold mono">{v}</div></div>); }
